const isEmail = require('isemail');
const unsafePattern = /(<|>|{|}|((https?:)?\/\/|www\.)[^\/\s]+|{{.*}})/i;

export default angular
  .module('doxy.common.components.form', ['validation'])
  .run(function (
    $validation,
    $q,
    $translate,
    User,
    Room,
    Institution,
    notificationFactory,
    institutionHelper
  ) {
    $validation.showSuccessMessage = false;

    function isUnique(Model, params, element) {
      element.addClass('loading');
      const deferred = $q.defer();
      Model.findOne({
        filter: {
          where: params,
          fields: ['id'],
        },
      })
        .$promise.then(
          // eslint-disable-next-line promise/always-return
          () => {
            deferred.reject(false);
          },
          (err) => {
            if (err.status === 404) {
              deferred.resolve(true);
            } else {
              deferred.reject(false);
            }
          }
        )
        .finally(element.removeClass.bind(element, 'loading'));
      return deferred.promise;
    }

    const expression = {
      email: function (value) {
        return isEmail.validate(value);
      },
      number: /^\d+$/,
      alphanumeric: /^[a-zA-Z0-9]*$/,
      domain: /^[a-zA-Z0-9]*$/,
      urlProtocolPattern: new RegExp('^http(s?):/{2}', 'i'),
      urlPattern: new RegExp(
        '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name and extension
          '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
          '(\\:\\d+)?' + // port
          '(\\/[-a-z#\\d%@_.~+&:]*)*' + // path
          '(\\?[;&a-z\\d%@_.,~+&:=-]*)?' + // query string
          '(\\#[-a-z\\d_]*)?$',
        'i'
      ), // fragment locator
      npi: function (value) {
        if (value) {
          return value.match(expression.number) && value.length === 10;
        }

        return true;
      },

      required: function (value) {
        return !!value;
      },

      emailOrEmpty: function (value) {
        if (value) {
          return isEmail.validate(value);
        }
        return true;
      },

      multipleEmails: function (value) {
        const emailsArray = value.split(/\s*,\s*/);
        return emailsArray.every((i) => isEmail.validate(i));
      },

      uniqueEmail: function (value, scope, element, attrs) {
        if (attrs.dOriginalValue && attrs.dOriginalValue === value) return true;
        element.addClass('loading');
        return User.unique({
          email: value,
        })
          .$promise.then(function (result) {
            element.removeClass('loading');
            return result.unique;
          })
          .catch(function (err) {
            if (err.status === 429)
              notificationFactory.showCommon('error', 'tooManyAttemptsSignUp');
            if (err.status >= 500 || err.status === -1) {
              notificationFactory.showCommon(
                'error',
                '500',
                'signup-server-error'
              );
              return Promise.resolve({ unique: true }); // do nothing on server error
            }
          });
      },

      emailMatch: function (value, scope, element, attrs) {
        return value.toLowerCase() === attrs.dMatch.toLowerCase();
      },

      urlProtocol: function (value) {
        if (!value) {
          return true;
        }
        return expression.urlProtocolPattern.test(value);
      },

      url: function (value, scope, element, attrs) {
        if (!value) {
          return true;
        }
        return expression.urlPattern.test(value);
      },

      safe: function (value) {
        return !unsafePattern.test(value);
      },

      uniqueRoom: function (value, scope, element, attrs) {
        if (Boolean(attrs.dOriginalValue) && attrs.dOriginalValue === value) {
          return true;
        }

        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        return new Promise(async (resolve, reject) => {
          element.addClass('loading');

          try {
            const institution = await institutionHelper.getCurrentInstitution();
            const institutionId = Boolean(institution) ? institution.id : null;
            const result = await Room.isUnique({
              slug: value,
              institutionId,
            }).$promise;

            if (Boolean(result) && Boolean(result.isUnique)) {
              resolve(true);
            } else {
              // eslint-disable-next-line prefer-promise-reject-errors
              reject(false);
            }
          } catch (e) {
            reject(e);
          }

          try {
            element.removeClass('loading');
          } catch {}
        });
      },

      uniqueDomain: function (value, scope, element, attrs) {
        if (attrs.dOriginalValue && attrs.dOriginalValue === value) return true;
        return isUnique(Institution, { domain: value }, element);
      },

      validPassword: function (value, scope, element) {
        element.addClass('loading');
        return User.prototype$isValidPassword(
          { id: User.getCurrentId() },
          { password: value }
        ).$promise.then(function (result) {
          element.removeClass('loading');
          return result.isValid;
        });
      },

      passwordMatch: function (value, scope, element, attrs) {
        return value === attrs.dMatch;
      },

      isLong: function (value) {
        return value.length >= 20;
      },

      displayName: function (value) {
        return value.length <= 20;
      },

      minLength: function (value, scope, element, attrs) {
        return value.length >= attrs.validatorMinLength;
      },
      clinicNameRequired: Boolean,
      clinicDomainRequired: Boolean,
      clinicNameMinLength: function (value, scope, element, attrs) {
        return value.length >= attrs.validatorMinLength;
      },
      clinicSubdomainMinLength: function (value, scope, element, attrs) {
        return value.length >= attrs.validatorMinLength;
      },
      alphanumericClinicDomain: /^[\dA-Za-z]*$/u,
      clinicUniqueCheckinLink: function (value, scope, element, attrs) {
        if (Boolean(attrs.dOriginalValue) && attrs.dOriginalValue === value) {
          return true;
        }
        return isUnique(Institution, { domain: value }, element);
      },
    };

    const defaultMsg = {};
    const basePath = 'form.errors.';
    const keys = Object.keys(expression);
    const translationKeys = keys.map((key) => `${basePath}${key}`);

    // Create empty placeholders populated later once locales are loaded
    // So $validation library doesn't complaint for undefined messages until then
    const dummyDefaultMsg = keys.reduce((res, key) => {
      res[key] = { error: '', success: '' };
      return res;
    }, {});

    $validation.expression = expression;
    $validation
      .setExpression($validation.expression)
      .setDefaultMsg(dummyDefaultMsg);

    $translate(translationKeys).then(function (translations) {
      keys.forEach(function (key) {
        defaultMsg[key] = {
          error: translations[`${basePath}${key}`],
          success: '',
        };
      });
      $validation.setDefaultMsg(defaultMsg);
    });
  })
  .directive('dValidator', function ($compile) {
    return {
      restrict: 'A',
      priority: 1001,
      terminal: true, // prevent lower priority directives to compile after it

      compile: function (tElement, tAttrs) {
        tElement.removeAttr('d-validator');
        tElement.attr({
          'valid-method': 'blur',
          'initial-validity': true,
          validator: tAttrs.dValidator,
          name: tAttrs.ngModel.split('.').pop(),
        });
        const fn = $compile(tElement);
        return function (scope) {
          fn(scope);
        };
      },
    };
  }).name;
